import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MobileCardsAccountsPage.css';

const CARDS_KEY = 'mobile-saved-cards';
const PAYMENTS_KEY = 'mobile-card-payments-log';
const REFUNDS_KEY = 'mobile-card-refunds-log';

const readJson = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : fallback;
  } catch (_) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    /* ignore */
  }
};

const MobileCardsAccountsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('payments');
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [cards, setCards] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', number: '', expiry: '' });

  const reload = useCallback(() => {
    setCards(readJson(CARDS_KEY, []));
    setPayments(readJson(PAYMENTS_KEY, []));
    setRefunds(readJson(REFUNDS_KEY, []));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const saveCard = (e) => {
    e.preventDefault();
    const digits = (form.number || '').replace(/\D/g, '');
    const last4 = digits.slice(-4) || '0000';
    const entry = {
      id: `c-${Date.now()}`,
      holder: (form.name || 'Cardholder').trim(),
      last4,
      brand: digits.startsWith('4') ? 'Visa' : digits.startsWith('5') ? 'Mastercard' : 'Card',
      addedAt: Date.now(),
    };
    setCards((prev) => {
      const next = [entry, ...prev];
      writeJson(CARDS_KEY, next);
      return next;
    });
    setForm({ name: '', number: '', expiry: '' });
    setShowAdd(false);
  };

  const rows = tab === 'payments' ? payments : refunds;

  return (
    <div className="mca-page">
      <header className="mca-header">
        <button type="button" className="mca-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mca-title">Cards &amp; accounts</h1>
      </header>

      <button type="button" className="mca-add-card" onClick={() => setShowAdd(true)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Add a new card
      </button>

      {cards.length > 0 && (
        <section className="mca-saved" aria-label="Saved cards">
          <p className="mca-saved-label">Saved cards</p>
          {cards.map((c) => (
            <div key={c.id} className="mca-card-row">
              <span>{c.brand} ···· {c.last4}</span>
              <span style={{ color: '#888', fontSize: 12 }}>{c.holder}</span>
            </div>
          ))}
        </section>
      )}

      <div className="mca-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'payments'}
          className={`mca-tab ${tab === 'payments' ? 'is-active' : ''}`}
          onClick={() => setTab('payments')}
        >
          Payments
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'refunds'}
          className={`mca-tab ${tab === 'refunds' ? 'is-active' : ''}`}
          onClick={() => setTab('refunds')}
        >
          Refunds
        </button>
      </div>

      <div className="mca-tab-body">
        {rows.length === 0 ? (
          <p className="mca-no-data">No data</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map((row) => (
              <li key={row.id} className="mca-card-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{row.title || row.description}</span>
                <span style={{ fontSize: 12, color: '#888' }}>{row.date}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAdd && (
        <div
          className="mca-overlay"
          role="presentation"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) setShowAdd(false);
          }}
        >
          <form className="mca-modal" onSubmit={saveCard} onClick={(e) => e.stopPropagation()}>
            <h2>Add a new card</h2>
            <div className="mca-field">
              <label htmlFor="mca-name">Name on card</label>
              <input
                id="mca-name"
                autoComplete="cc-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Alex Gender"
              />
            </div>
            <div className="mca-field">
              <label htmlFor="mca-num">Card number</label>
              <input
                id="mca-num"
                inputMode="numeric"
                autoComplete="cc-number"
                value={form.number}
                onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
                placeholder="4242 4242 4242 4242"
              />
            </div>
            <div className="mca-field">
              <label htmlFor="mca-exp">Expiry (MM/YY)</label>
              <input
                id="mca-exp"
                autoComplete="cc-exp"
                value={form.expiry}
                onChange={(e) => setForm((f) => ({ ...f, expiry: e.target.value }))}
                placeholder="12/28"
              />
            </div>
            <div className="mca-modal-actions">
              <button type="button" className="mca-btn-cancel" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
              <button type="submit" className="mca-btn-save">
                Save card
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default MobileCardsAccountsPage;
